import React, { useEffect, useMemo, useState } from 'react'
import {
    Col, Divider, Form, Input, Modal,
    Row,
    InputNumber,
    DatePicker,
    AutoComplete,
    Button,
    Space,
    Select,
    Table,
    Checkbox,
    Tabs,
    Tooltip,
    Typography,
    message
} from 'antd';
import { ContactsOutlined, CheckCircleOutlined, EditOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { GetAPI, PostAPI, loadConsignee, loadHsCode } from '../../../../../../redux';
import jnsDokumen from '../../../../../../assets/jenis_dokument.json';
import kodeKantor from '../../../../../../assets/kode_kantor.json';
import countries from '../../../../../../assets/countries.json';

const { Option } = Select;
const { TabPane } = Tabs;

function BillModal({
    isDetilModalVisible, handleDetilModalCancel, model,
    modalDetilForm, handleDetilModalOk, formatAwbNumber, arps, hscodeCache,
    cns, form, kdKemas, handleBack, editingDetilIndex, handleNext, detilList,
    handleSelectDetil
}) {
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [shipperAddresses, setShipperAddresses] = useState([]);
    const [consigneeAddresses, setConsigneeAddresses] = useState([]);
    const [msgTelex, setMsgTelex] = useState({});
    const [isHsModalVisible, setIsHsModalVisible] = useState(false);
    const [hsModalForm] = Form.useForm();
    const [editingHsFieldIndex, setEditingHsFieldIndex] = useState(null);
    const [isDokumenModalVisible, setIsDokumenModalVisible] = useState(false);
    const [dokumenModalForm] = Form.useForm();
    const [editingDokumenFieldIndex, setEditingDokumenFieldIndex] = useState(null);
    const [isKontainerModalVisible, setIsKontainerModalVisible] = useState(false);
    const [kontainerModalForm] = Form.useForm();
    const [editingKontainerFieldIndex, setEditingKontainerFieldIndex] = useState(null);
    const [isMasterConsigneeModalVisible, setIsMasterConsigneeModalVisible] = useState(false);
    const [masterConsigneeTarget, setMasterConsigneeTarget] = useState(null);
    const [masterConsigneeTab, setMasterConsigneeTab] = useState('select');
    const [consigneeSearchKeyword, setConsigneeSearchKeyword] = useState('');
    const [editingMasterConsignee, setEditingMasterConsignee] = useState(null);
    const [masterConsigneeForm] = Form.useForm();
    const [isMasterHsModalVisible, setIsMasterHsModalVisible] = useState(false);
    const [masterHsTab, setMasterHsTab] = useState('select');
    const [hsSearchKeyword, setHsSearchKeyword] = useState('');
    const [editingMasterHs, setEditingMasterHs] = useState(null);
    const [masterHsForm] = Form.useForm();
    const airportOptions = useMemo(() => {
        if (!Array.isArray(arps)) return [];
        return arps.map(item => {
            const countryCode = `${item?.country || ''}`.toUpperCase();
            const countryName = item?.country_name || item?.country_desc || item?.kota || '';
            const airportCode = `${item?.code || ''}`.toUpperCase();
            const airportName = item?.name || '';
            const labelParts = [];
            if (countryCode) {
                labelParts.push(countryCode);
            }
            if (countryName) {
                labelParts.push(`(${countryName})`);
            }
            if (airportCode) {
                labelParts.push(airportCode);
            }
            if (airportName) {
                labelParts.push(`(${airportName})`);
            }
            const label = labelParts.join(' ').replace(/\s+/g, ' ').trim();
            return {
                ...item,
                value: item?.value || `${countryCode}${airportCode}`,
                label: label || item?.value,
            };
        });
    }, [arps]);
    const airportDetailMap = useMemo(() => {
        return airportOptions.reduce((acc, option) => {
            if (option?.value) {
                acc[option.value] = option?.label || option?.value;
            }
            return acc;
        }, {});
    }, [airportOptions]);
    const filterAirportOption = (inputValue = '', option = {}) => {
        const regex = new RegExp(inputValue, 'i');
        return regex.test(option?.value || '') || regex.test(option?.label || '');
    };
    const pelabuhanAsalValue = Form.useWatch('pelabuhan_asal', modalDetilForm);
    const pelabuhanTransitValue = Form.useWatch('pelabuhan_transit', modalDetilForm);
    const pelabuhanBongkarValue = Form.useWatch('pelabuhan_bongkar', modalDetilForm);
    const pelabuhanAkhirValue = Form.useWatch('pelabuhan_akhir', modalDetilForm);
    const renderAirportDetail = (value) => {
        const detail = value ? airportDetailMap[value] : '';
        if (!detail) return null;
        return (
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                {detail}
            </Typography.Text>
        );
    };
    const dispatch = useDispatch();
    const kdKelompokPosValue = Form.useWatch('kd_kelompok_pos', modalDetilForm);
    const consigneeList = useMemo(() => Array.isArray(cns) ? cns : [], [cns]);
    const targetLabelMap = {
        shipper: 'Shipper',
        consignee: 'Consignee',
        notify: 'Notify'
    };
    const extractAddressList = (item = {}) => [
        item?.address,
        item?.address2 || "",
        item?.address3 || "",
        item?.address4 || "",
        item?.address5 || ""
    ].filter(addr => addr);
    const normalizeConsigneeOption = (item = {}) => {
        const addressAll = extractAddressList(item);
        return {
            label: item?.name,
            value: item?.name,
            address: item?.address,
            addressAll,
            npwp: item?.npwp,
            country: item?.country
        };
    };
    const masterFieldMap = {
        shipper: {
            name: 'nama_shipper',
            npwp: 'npwp_shipper',
            address: 'almt_shipper',
            country: 'neg_shipper',
            addressSetter: setShipperAddresses,
        },
        consignee: {
            name: 'nama_consignee',
            npwp: 'npwp_consignee',
            address: 'almt_consignee',
            country: 'neg_consignee',
            addressSetter: setConsigneeAddresses,
        },
        notify: {
            name: 'nama_notify',
            npwp: 'npwp_notify',
            address: 'almt_notify',
            country: 'neg_notify'
        }
    };

    const handleSearch = (value = '') => {
        const keyword = `${value || ''}`.toLowerCase();
        const results = consigneeList
            .filter(item => !keyword || item?.name?.toLowerCase()?.includes(keyword))
            .map(normalizeConsigneeOption);
        setFilteredOptions(results);
    };

    useEffect(() => {
        setFilteredOptions(consigneeList.map(normalizeConsigneeOption));
    }, [consigneeList]);

    const handleSelectConsigneeAutocomplete = (targetKey, option = {}) => {
        const targetConfig = masterFieldMap[targetKey];
        if (!targetConfig) return;
        const nextValues = {
            [targetConfig.npwp]: option?.npwp || '',
            [targetConfig.address]: option?.address || '',
        };
        if (targetConfig.country) {
            nextValues[targetConfig.country] = option?.country || '';
        }
        modalDetilForm.setFieldsValue(nextValues);
        if (typeof targetConfig.addressSetter === 'function') {
            targetConfig.addressSetter(option?.addressAll?.filter(addr => addr) || []);
        }
    };

    const handleChangeConsigneeAutocomplete = (targetKey, value) => {
        if (value !== undefined && value !== null && value !== '') return;
        const targetConfig = masterFieldMap[targetKey];
        if (!targetConfig) return;
        const resetValues = {
            [targetConfig.npwp]: '',
            [targetConfig.address]: '',
        };
        if (targetConfig.country) {
            resetValues[targetConfig.country] = '';
        }
        modalDetilForm.setFieldsValue(resetValues);
        if (typeof targetConfig.addressSetter === 'function') {
            targetConfig.addressSetter([]);
        }
    };

    const fectAwbData = () => {
        if (model.detil[editingDetilIndex]?.no_master_blawb) {
            dispatch(GetAPI({ url: `api/v1/rskp/awb_detil/${model.detil[editingDetilIndex]?.no_master_blawb}` }))
                .then(resp => {
                    setMsgTelex(resp?.payload?.data || {})
                })
        }
    }

    const normalizeKontainerItem = (item = {}, fallbackDetailId = '') => {
        const getValue = (key) => item[key] ?? item[key.replace(/_/g, ' ')] ?? '';
        return {
            id_kontainer: getValue('id_kontainer'),
            id_detil: getValue('id_detil') || fallbackDetailId,
            seri_kontainer: getValue('seri_kontainer'),
            nomor_kontainer: getValue('nomor_kontainer'),
            ukuran_kontainer: getValue('ukuran_kontainer'),
            tipe_kontainer: getValue('tipe_kontainer'),
            jenis_kontainer: getValue('jenis_kontainer'),
            nomor_segel: getValue('nomor_segel'),
            status_kontainer: getValue('status_kontainer'),
        };
    };

    useEffect(() => {
        if (!kdKelompokPosValue) return;

        const editingRecord = (editingDetilIndex !== null && editingDetilIndex >= 0)
            ? detilList?.[editingDetilIndex]
            : null;

        if (editingRecord && editingRecord?.kd_kelompok_pos === kdKelompokPosValue) {
            const currentNoPos = modalDetilForm.getFieldValue('no_pos');
            if (currentNoPos !== editingRecord?.no_pos) {
                modalDetilForm.setFieldsValue({ no_pos: editingRecord?.no_pos });
            }
            return;
        }

        const existingNoPos = (model?.detil || [])
            .filter(item => item?.kd_kelompok_pos === kdKelompokPosValue)
            .map(item => {
                const numericStr = `${item?.no_pos ?? ''}`.replace(/[^0-9]/g, '');
                return parseInt(numericStr || '0', 10);
            })
            .filter(num => !Number.isNaN(num));

        const lastNumber = existingNoPos.length ? Math.max(...existingNoPos) : 0;
        const generatedNoPos = (lastNumber + 1).toString().padStart(3, '0');
        if (modalDetilForm.getFieldValue('no_pos') !== generatedNoPos) {
            modalDetilForm.setFieldsValue({ no_pos: generatedNoPos });
        }
    }, [kdKelompokPosValue, modalDetilForm, model?.detil, editingDetilIndex, detilList]);

    useEffect(() => {
        if (detilList && editingDetilIndex != null) {
            const currentRecord = detilList[editingDetilIndex];
            const barang = model.barang.find(item => item?.id_detil === currentRecord?.id_detil);

            if (currentRecord) {
                modalDetilForm.resetFields();
                modalDetilForm.setFieldsValue({ ...currentRecord, ...barang });

                const detailId = currentRecord?.id_detil;
                const recordDokumen = currentRecord?.dokumen;
                const modelDokumen = Array.isArray(model?.dokumen)
                    ? model.dokumen.filter(doc => doc.id_detil === detailId || doc['id detil'] === detailId)
                    : [];
                const dokumenData = recordDokumen?.length ? recordDokumen : modelDokumen;
                modalDetilForm.setFieldsValue({ dokumen: dokumenData?.length ? dokumenData : [] });

                const recordKontainer = currentRecord?.kontainer;
                const modelKontainer = Array.isArray(model?.kontainer)
                    ? model.kontainer.filter(kon => kon.id_detil === detailId || kon['id detil'] === detailId)
                    : [];
                const kontainerData = (recordKontainer?.length ? recordKontainer : modelKontainer)?.map(item => normalizeKontainerItem(item, detailId));
                modalDetilForm.setFieldsValue({ kontainer: kontainerData });

                if (currentRecord.nama_shipper) {
                    const found = consigneeList.find(item => item.name === currentRecord.nama_shipper);
                    if (found) {
                        const addressAll = extractAddressList(found);
                        setShipperAddresses(addressAll);
                        setFilteredOptions([normalizeConsigneeOption(found)]);
                    }
                }

                if (currentRecord.nama_consignee) {
                    const found = consigneeList.find(item => item.name === currentRecord.nama_consignee);
                    if (found) {
                        const addressAll = extractAddressList(found);
                        setConsigneeAddresses(addressAll);
                        setFilteredOptions([normalizeConsigneeOption(found)]);
                    }
                }
            }
        }
    }, [editingDetilIndex, detilList, consigneeList, model.barang, model.dokumen, model.kontainer]);

    useEffect(() => {
        if (isDetilModalVisible === true) {
            fectAwbData();
        }
    }, [isDetilModalVisible, editingDetilIndex])

    const isConsole = Form.useWatch("isconsole", modalDetilForm);
    const isPartial = Form.useWatch("ispartial", modalDetilForm);
    const jumlahKemasan = Form.useWatch("jumlah_kemasan", modalDetilForm);
    const hsCodesValues = Form.useWatch("hs_codes", modalDetilForm) || [];
    const dokumenValues = Form.useWatch("dokumen", modalDetilForm) || [];
    const kontainerValues = Form.useWatch("kontainer", modalDetilForm) || [];
    const awbOptions = useMemo(() => {
        if (!Array.isArray(detilList)) return [];
        return detilList
            .map((item = {}, index) => ({
                value: item?.id_detil,
                label: [item?.no_master_blawb || item?.no_host_blawb || '-', item?.no_pos ? `Pos ${item.no_pos}` : `Data ${index + 1}`]
                    .filter(Boolean)
                    .join(' • '),
            }))
            .filter(option => option.value);
    }, [detilList]);
    const currentDetilRecord =
        Array.isArray(detilList) && editingDetilIndex !== null && editingDetilIndex >= 0
            ? detilList[editingDetilIndex]
            : undefined;
    const currentAwbSelection = currentDetilRecord?.id_detil;
    const watchedTotalKemasan = Form.useWatch("total_kemasan", modalDetilForm);
    const normalizeNumeric = (value) => {
        if (value === undefined || value === null || value === '') return null;
        const numericValue = typeof value === 'number' ? value : parseFloat(value);
        return Number.isNaN(numericValue) ? null : numericValue;
    };
    const totalKemasan = normalizeNumeric(watchedTotalKemasan) ?? normalizeNumeric(currentDetilRecord?.total_kemasan);

    const handleSelectAwb = (value) => {
        if (!value) return;
        if (typeof handleSelectDetil === 'function') {
            handleSelectDetil(value);
        }
    };

    const handleOpenHsModal = (initialValues = null, fieldIndex = null) => {
        hsModalForm.resetFields();
        if (initialValues) {
            hsModalForm.setFieldsValue(initialValues);
        }
        setEditingHsFieldIndex(fieldIndex);
        setIsHsModalVisible(true);
    };

    const handleSelectHsAutocomplete = (value, option = {}) => {
        if (!value) return;
        hsModalForm.setFieldsValue({
            hs_code: value,
            uraian_barang: option?.record?.hs_code_description?.toUpperCase() || hsModalForm.getFieldValue('uraian_barang') || '',
        });
    };

    const handleCloseHsModal = () => {
        hsModalForm.resetFields();
        setEditingHsFieldIndex(null);
        setIsHsModalVisible(false);
    };

    const handleOpenDokumenModal = (initialValues = null, fieldIndex = null) => {
        dokumenModalForm.resetFields();
        if (initialValues) {
            const valuesToSet = { ...initialValues };
            if (valuesToSet?.tanggal_dokumen) {
                valuesToSet.tanggal_dokumen = moment(valuesToSet.tanggal_dokumen, 'DD-MM-YYYY');
            }
            dokumenModalForm.setFieldsValue(valuesToSet);
        }
        setEditingDokumenFieldIndex(fieldIndex);
        setIsDokumenModalVisible(true);
    };

    const handleCloseDokumenModal = () => {
        dokumenModalForm.resetFields();
        setEditingDokumenFieldIndex(null);
        setIsDokumenModalVisible(false);
    };

    const currentDetailId = () => {
        const currentRecord = detilList[editingDetilIndex];
        return currentRecord?.id_detil || modalDetilForm.getFieldValue('id_detil');
    }

    const masterConsigneeData = useMemo(() => {
        const keyword = consigneeSearchKeyword.trim().toLowerCase();
        if (!keyword) return consigneeList;
        return consigneeList.filter(item => {
            const candidates = [
                item?.name,
                item?.npwp,
                item?.country,
                item?.address,
            ];
            return candidates.some(value => `${value || ''}`.toLowerCase().includes(keyword));
        });
    }, [consigneeSearchKeyword, consigneeList]);

    const masterHsData = useMemo(() => {
        const list = Array.isArray(hscodeCache) ? hscodeCache : [];
        const keyword = hsSearchKeyword.trim().toLowerCase();
        if (!keyword) return list;
        return list.filter(item => {
            const candidates = [item?.hs_code, item?.hs_code_description];
            return candidates.some(value => `${value || ''}`.toLowerCase().includes(keyword));
        });
    }, [hscodeCache, hsSearchKeyword]);

    const hsAutocompleteOptions = useMemo(() => {
        if (!Array.isArray(hscodeCache)) return [];
        return hscodeCache
            .map(item => ({
                value: item?.hs_code || '',
                label: `${item?.hs_code || ''} - ${item?.hs_code_description || ''}`.toUpperCase().trim(),
                record: item,
            }))
            .filter(option => option.value);
    }, [hscodeCache]);

    const handleResetMasterConsigneeForm = () => {
        masterConsigneeForm.resetFields();
        setEditingMasterConsignee(null);
    };

    const handleOpenMasterConsigneeModal = (targetKey) => {
        setMasterConsigneeTarget(targetKey);
        setMasterConsigneeTab('select');
        setConsigneeSearchKeyword('');
        handleResetMasterConsigneeForm();
        setIsMasterConsigneeModalVisible(true);
    };

    const handleCloseMasterConsigneeModal = () => {
        setIsMasterConsigneeModalVisible(false);
        setMasterConsigneeTarget(null);
        setMasterConsigneeTab('select');
        setConsigneeSearchKeyword('');
        handleResetMasterConsigneeForm();
    };

    const handleSelectConsigneeFromMaster = (record = {}) => {
        const targetConfig = masterFieldMap[masterConsigneeTarget];
        if (!targetConfig) {
            message.warning('Pilih tujuan pengisian terlebih dahulu');
            return;
        }
        const addressAll = extractAddressList(record);
        const nextValues = {
            [targetConfig.name]: record?.name || '',
            [targetConfig.npwp]: record?.npwp || '',
            [targetConfig.address]: record?.address || '',
        };
        if (targetConfig.country) {
            nextValues[targetConfig.country] = record?.country || '';
        }
        modalDetilForm.setFieldsValue(nextValues);
        if (typeof targetConfig.addressSetter === 'function') {
            targetConfig.addressSetter(addressAll);
        }
        setFilteredOptions([normalizeConsigneeOption(record)]);
        handleCloseMasterConsigneeModal();
    };

    const handleEditMasterConsignee = (record = {}) => {
        setMasterConsigneeTab('add');
        setEditingMasterConsignee(record);
        masterConsigneeForm.setFieldsValue({
            name: record?.name || '',
            npwp: record?.npwp || '',
            country: record?.country || '',
            address: record?.address || '',
        });
    };

    const handleSubmitMasterConsignee = (values) => {
        const payload = editingMasterConsignee
            ? {
                ...editingMasterConsignee,
                ...values,
                _id: editingMasterConsignee?._id || editingMasterConsignee?.id,
            }
            : values;
        dispatch(PostAPI({ url: 'api/v1/consignee', data: payload }))
            .then(resp => {
                if (resp?.payload) {
                    message.success(editingMasterConsignee ? 'Data consignee berhasil diperbarui' : 'Data consignee berhasil disimpan');
                    dispatch(loadConsignee());
                    handleResetMasterConsigneeForm();
                    setMasterConsigneeTab('select');
                    setConsigneeSearchKeyword(values?.name || '');
                }
            });
    };

    const renderMasterConsigneeIcon = (targetKey) => (
        <Tooltip title="Pilih dari Master Consignee">
            <ContactsOutlined
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                    e.stopPropagation();
                    handleOpenMasterConsigneeModal(targetKey);
                }}
                style={{ color: '#1890ff', cursor: 'pointer' }}
            />
        </Tooltip>
    );

    const handleResetMasterHsForm = () => {
        masterHsForm.resetFields();
        setEditingMasterHs(null);
    };

    const handleOpenMasterHsModal = () => {
        setMasterHsTab('select');
        setHsSearchKeyword('');
        handleResetMasterHsForm();
        setIsMasterHsModalVisible(true);
    };

    const handleCloseMasterHsModal = () => {
        setIsMasterHsModalVisible(false);
        setMasterHsTab('select');
        setHsSearchKeyword('');
        handleResetMasterHsForm();
    };

    const handleSelectHsFromMaster = (record = {}) => {
        hsModalForm.setFieldsValue({
            hs_code: record?.hs_code || '',
            uraian_barang: record?.hs_code_description?.toUpperCase() || '',
        });
        handleCloseMasterHsModal();
    };

    const handleEditMasterHs = (record = {}) => {
        setMasterHsTab('add');
        setEditingMasterHs(record);
        masterHsForm.setFieldsValue({
            hs_code: record?.hs_code || '',
            hs_code_description: record?.hs_code_description || '',
        });
    };

    const handleSubmitMasterHs = (values) => {
        const payload = editingMasterHs
            ? {
                ...editingMasterHs,
                ...values,
                _id: editingMasterHs?._id || editingMasterHs?.id,
            }
            : values;
        dispatch(PostAPI({ url: 'api/v1/hs_code', data: payload }))
            .then(resp => {
                if (resp?.payload) {
                    message.success(editingMasterHs ? 'HS Code berhasil diperbarui' : 'HS Code berhasil disimpan');
                    dispatch(loadHsCode());
                    setMasterHsTab('select');
                    setHsSearchKeyword(values?.hs_code || '');
                    handleResetMasterHsForm();
                }
            });
    };

    const handleMasterHsTabChange = (key) => {
        setMasterHsTab(key);
        if (key !== 'add') {
            handleResetMasterHsForm();
        }
    };

    const renderMasterHsIcon = () => (
        <Tooltip title="Pilih dari Master HS Code">
            <ContactsOutlined
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                    e.stopPropagation();
                    handleOpenMasterHsModal();
                }}
                style={{ color: '#1890ff', cursor: 'pointer' }}
            />
        </Tooltip>
    );

    const handleOpenKontainerModal = (initialValues = null, fieldIndex = null) => {
        kontainerModalForm.resetFields();
        const defaults = initialValues
            ? normalizeKontainerItem(initialValues, initialValues.id_detil || currentDetailId() || '')
            : {
                id_detil: currentDetailId() || '',
            };
        if (!defaults.id_kontainer) {
            defaults.id_kontainer = '';
        }
        kontainerModalForm.setFieldsValue(defaults);
        setEditingKontainerFieldIndex(fieldIndex);
        setIsKontainerModalVisible(true);
    };

    const handleCloseKontainerModal = () => {
        kontainerModalForm.resetFields();
        setEditingKontainerFieldIndex(null);
        setIsKontainerModalVisible(false);
    };

    const handleConfirmCloseModal = () => {
        Modal.confirm({
            title: 'Konfirmasi',
            content: 'Ya jika ingin close?',
            okText: 'Ya',
            cancelText: 'Tidak',
            onOk: () => {
                handleDetilModalCancel();
            },
        });
    };

    return (
        <div>
            <Modal
                title="Bill Of Lading"
                visible={isDetilModalVisible}
                onOk={() => modalDetilForm.submit()}
                onCancel={handleConfirmCloseModal}
                okText="Simpan"
                cancelText="Batal"
                width="80%"
                style={{ marginTop: -50 }}
                footer={[
                    <Button key="cancel" onClick={handleConfirmCloseModal}>
                        Batal
                    </Button>,
                    <Button key="submit" type="primary" onClick={() => modalDetilForm.submit()}>
                        Simpan
                    </Button>,
                ]}
            >
                <Form form={modalDetilForm} layout="vertical" onFinish={handleDetilModalOk}>
                    {editingDetilIndex !== null && editingDetilIndex >= 0 && (
                        <Space style={{ marginBottom: 16 }} wrap align="center">
                            <span>Data ke {editingDetilIndex + 1} dari {detilList?.length}</span>
                            {awbOptions.length > 0 && (
                                <Select
                                    showSearch
                                    placeholder="Pilih AWB"
                                    optionFilterProp="label"
                                    style={{ minWidth: 240 }}
                                    value={currentAwbSelection}
                                    onChange={handleSelectAwb}
                                >
                                    {awbOptions.map(option => (
                                        <Option key={option.value} value={option.value} label={option.label}>
                                            {option.label}
                                        </Option>
                                    ))}
                                </Select>
                            )}
                            <Space>
                                <Button key="prev" disabled={editingDetilIndex <= 0} onClick={handleBack}>Sebelumnya</Button>
                                <Button key="next" disabled={editingDetilIndex >= detilList.length - 1} onClick={handleNext}>Selanjutnya</Button>
                            </Space>
                        </Space>
                    )}
                    <Row gutter={24}>
                        <Col span={12}>
                            <Divider orientation="left" plain style={{ fontWeight: 'bold' }}>
                                Airwaybill
                            </Divider>

                            <Form.Item
                                label="Kelompok Pos"
                                name="kd_kelompok_pos"
                            >
                                <Select placeholder="">
                                    <Option value="01">01 - Barang RKSP / Barang yang diselesaikan di tempat</Option>
                                    <Option value="02">02 - Barang import angkut lanjut</Option>
                                    <Option value="03">03 - Barang import angkut terus</Option>
                                    <Option value="04">04 - Barang export angkut lanjut</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Nomor Pos"
                                name="no_pos"
                                rules={[{ required: true, message: 'Nomor Pos wajib diisi' }]}
                                style={{ marginTop: -15 }}
                            >
                                <Input autoComplete="off" disabled />
                            </Form.Item>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Master Awb" name="no_master_blawb" style={{ marginTop: -15 }}
                                        rules={[
                                            {
                                                required: true,
                                                pattern: /^[A-Za-z0-9]+-[A-Za-z0-9]+$/,
                                                message: 'Nomor mawb harus dalam format 000-0000000',
                                            },
                                        ]}
                                        getValueFromEvent={(e) => formatAwbNumber(e.target.value = e.target.value.toUpperCase())}
                                    >
                                        <Input autoComplete="off" onChange={(e) => {
                                            const v = formatAwbNumber(e.target.value.toUpperCase());
                                            modalDetilForm.setFieldsValue({
                                                no_master_blawb: v,
                                                no_host_blawb: v,
                                            });
                                        }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="Tanggal Master Awb"
                                        name="tgl_master_blawb"
                                        rules={[{ required: true, message: 'Tanggal Master Awb wajib di isi' }]}
                                        getValueProps={(value) => ({
                                            value: value ? moment(value, 'DD-MM-YYYY') : null,
                                        })}
                                        getValueFromEvent={(_, dateString) => dateString}
                                        style={{ marginTop: -15 }}
                                    >
                                        <DatePicker
                                            format="DD-MM-YYYY"
                                            style={{ width: '100%' }}
                                            placeholder=''
                                            onChange={(date, dateString) => {
                                                modalDetilForm.setFieldsValue({
                                                    tgl_master_blawb: dateString,
                                                    tgl_host_blawb: dateString
                                                });
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="House Awb" name="no_host_blawb" style={{ marginTop: -15 }}>
                                        <Input autoComplete="off" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="Tanggal House Awb"
                                        name="tgl_host_blawb"
                                        rules={[{ required: true, message: 'Tanggal House Awb wajib di isi' }]}
                                        getValueProps={(value) => ({
                                            value: value ? moment(value, 'DD-MM-YYYY') : null,
                                        })}
                                        getValueFromEvent={(_, dateString) => dateString}
                                        style={{ marginTop: -15 }}
                                    >
                                        <DatePicker
                                            format="DD-MM-YYYY"
                                            style={{ width: '100%' }}
                                            placeholder=''
                                            onChange={(date, dateString) => {
                                                modalDetilForm.setFieldsValue({
                                                    tgl_master_blawb: dateString,
                                                    tgl_host_blawb: dateString
                                                });
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>

                        <Col span={12}>
                            <Divider orientation="left" plain style={{ fontWeight: 'bold' }}>
                                Bandara
                            </Divider>
                            <Form.Item
                                label="Bandara Asal"
                                name="pelabuhan_asal"
                                extra={renderAirportDetail(pelabuhanAsalValue)}
                            >
                                <AutoComplete
                                    maxLength={5}
                                    options={airportOptions}
                                    onInput={e => e.target.value = e.target.value.toUpperCase()}
                                    filterOption={filterAirportOption}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Bandara Transit"
                                name="pelabuhan_transit"
                                style={{ marginTop: -15 }}
                                extra={renderAirportDetail(pelabuhanTransitValue)}
                            >
                                <AutoComplete
                                    maxLength={5}
                                    options={airportOptions}
                                    onInput={e => e.target.value = e.target.value.toUpperCase()}
                                    filterOption={filterAirportOption}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Bandara Bongkar"
                                name="pelabuhan_bongkar"
                                style={{ marginTop: -15 }}
                                extra={renderAirportDetail(pelabuhanBongkarValue)}
                            >
                                <AutoComplete
                                    maxLength={5}
                                    options={airportOptions}
                                    onInput={e => e.target.value = e.target.value.toUpperCase()}
                                    filterOption={filterAirportOption}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Bandara akhir"
                                name="pelabuhan_akhir"
                                style={{ marginTop: -15 }}
                                extra={renderAirportDetail(pelabuhanAkhirValue)}
                            >
                                <AutoComplete
                                    maxLength={5}
                                    options={airportOptions}
                                    onInput={e => e.target.value = e.target.value.toUpperCase()}
                                    filterOption={filterAirportOption}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Divider orientation="left" plain style={{ fontWeight: 'bold' }}>
                                Shipper
                            </Divider>
                            <Form.Item
                                label="Name Shipper"
                                name="nama_shipper"
                                rules={[{ required: true, message: 'Name Shipper wajib diisi' }]}
                            >
                                <AutoComplete
                                    options={filteredOptions}
                                    onFocus={() => handleSearch('')}
                                    onSearch={handleSearch}
                                    onSelect={(value, option) => handleSelectConsigneeAutocomplete('shipper', option)}
                                    onChange={(value) => handleChangeConsigneeAutocomplete('shipper', value)}
                                    filterOption={false}
                                >
                                    <Input
                                        autoComplete="off"
                                        placeholder="Ketik atau pilih Shipper"
                                        suffix={renderMasterConsigneeIcon('shipper')}
                                    />
                                </AutoComplete>
                            </Form.Item>
                            <Form.Item label="NPWP Shipper" name="npwp_shipper" style={{ marginTop: -15 }}>
                                <Input
                                    maxLength={16}
                                    autoComplete="off"
                                />
                            </Form.Item>
                            <Form.Item
                                label="Address Shipper"
                                name="almt_shipper"
                                rules={[{ required: true, message: 'Address Shipper wajib diisi' }]}
                                style={{ marginTop: -15 }}
                            >
                                <AutoComplete
                                    options={shipperAddresses.map(addr => ({ value: addr }))}
                                    filterOption={(inputValue, option) =>
                                        option.value.toLowerCase().includes(inputValue.toLowerCase())
                                    }
                                >
                                    <Input.TextArea
                                        autoSize={{ minRows: 3, maxRows: 6 }}
                                        autoComplete="off"
                                        onInput={(e) => e.target.value = e.target.value.toUpperCase()}
                                    />
                                </AutoComplete>
                            </Form.Item>
                            <Form.Item
                                label="Negara Shipper"
                                name="neg_shipper" style={{ marginTop: -15 }}
                                rules={[{ required: true, message: 'Negara Shipper wajib diisi' }]}
                            >
                                <AutoComplete
                                    options={countries.map(item => ({
                                        value: item.code,
                                        label: `${item.code} - ${item.name}`
                                    }))}
                                    onInput={e => e.target.value = e.target.value.toUpperCase()}
                                    filterOption={(inputValue, option) =>
                                        option?.label?.toUpperCase().includes(inputValue.toUpperCase())
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Divider orientation="left" plain style={{ fontWeight: 'bold' }}>
                                Consignee
                            </Divider>
                            <Form.Item
                                label="Name Consignee"
                                name="nama_consignee"
                                rules={[{ required: true, message: 'Name Consignee wajib diisi' }]}
                            >
                                <AutoComplete
                                    options={filteredOptions}
                                    onFocus={() => handleSearch('')}
                                    onSearch={handleSearch}
                                    onSelect={(value, option) => handleSelectConsigneeAutocomplete('consignee', option)}
                                    onChange={(value) => handleChangeConsigneeAutocomplete('consignee', value)}
                                    filterOption={false}
                                >
                                    <Input
                                        autoComplete="off"
                                        placeholder="Ketik atau pilih Consignee"
                                        suffix={renderMasterConsigneeIcon('consignee')}
                                    />
                                </AutoComplete>
                            </Form.Item>
                            <Form.Item label="NPWP Consignee" name="npwp_consignee" style={{ marginTop: -15 }}
                                rules={[{ required: true, message: 'NPWP Consignee wajib diisi' }]}
                            >
                                <Input
                                    autoComplete="off"
                                    maxLength={16}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Address Consignee"
                                name="almt_consignee"
                                rules={[{ required: true, message: 'Address Consignee wajib diisi' }]}
                                style={{ marginTop: -15 }}
                            >
                                <AutoComplete
                                    options={consigneeAddresses.map(addr => ({ value: addr }))}
                                    filterOption={(inputValue, option) =>
                                        option.value.toLowerCase().includes(inputValue.toLowerCase())
                                    }
                                >
                                    <Input.TextArea
                                        onInput={(e) => e.target.value = e.target.value.toUpperCase()}
                                        autoSize={{ minRows: 3, maxRows: 6 }}
                                        autoComplete="off"
                                    />
                                </AutoComplete>
                            </Form.Item>
                            <Form.Item
                                label="Negara Consignee"
                                name="neg_consignee"
                                style={{ marginTop: -15 }}
                                rules={[{ required: true, message: 'Negara Consignee wajib diisi' }]}
                            >
                                <AutoComplete
                                    options={countries.map(item => ({
                                        value: item.code,
                                        label: `${item.code} - ${item.name}`
                                    }))}
                                    onInput={e => e.target.value = e.target.value.toUpperCase()}
                                    filterOption={(inputValue, option) =>
                                        option?.label?.toUpperCase().includes(inputValue.toUpperCase())
                                    }
                                />
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Divider orientation="left" plain style={{ fontWeight: 'bold' }}>
                                Telex Message
                            </Divider>
                            <div style={{ height: 800, overflowY: 'scroll', border: '1px solid #ccc', padding: 5, marginBottom: 5 }}>
                                {
                                    msgTelex?.message?.map((txt, idx) => (
                                        <div key={idx}>
                                            {((idx > 0) && (idx % 49) === 0) && (<div></div>)}
                                            <span variant="body2" component={"pre"} style={{ fontSize: 15 }}>{txt}</span>
                                        </div>
                                    ))
                                }
                            </div>
                        </Col>

                        <Col span={8} style={{ marginTop: -470 }}>
                            <Divider orientation="left" plain style={{ fontWeight: 'bold' }}>
                                Notify
                            </Divider>
                            <Form.Item label="Name Notify" name="nama_notify">
                                <AutoComplete
                                    options={filteredOptions}
                                    onFocus={() => handleSearch('')}
                                    onSearch={handleSearch}
                                    onSelect={(value, option) => handleSelectConsigneeAutocomplete('notify', option)}
                                    onChange={(value) => handleChangeConsigneeAutocomplete('notify', value)}
                                    filterOption={false}
                                >
                                    <Input
                                        autoComplete="off"
                                        placeholder="Ketik atau pilih Notify"
                                        suffix={renderMasterConsigneeIcon('notify')}
                                    />
                                </AutoComplete>
                            </Form.Item>
                            <Form.Item label="NPWP Notify" name="npwp_notify" style={{ marginTop: -15 }}>
                                <Input
                                    autoComplete="off"
                                    maxLength={16}
                                />
                            </Form.Item>
                            <Form.Item label="Address Notify" name="almt_notify" style={{ marginTop: -15 }}>
                                <Input.TextArea
                                    autoSize={{ minRows: 3, maxRows: 6 }}
                                    autoComplete="off"
                                    onInput={(e) => e.target.value = e.target.value.toUpperCase()}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Negara Notify"
                                name="neg_notify"
                                style={{ marginTop: -15 }}>
                                <AutoComplete
                                    options={countries.map(item => ({
                                        value: item.code,
                                        label: `${item.code} - ${item.name}`
                                    }))}
                                    onInput={e => e.target.value = e.target.value.toUpperCase()}
                                    filterOption={(inputValue, option) =>
                                        option?.label?.toUpperCase().includes(inputValue.toUpperCase())
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8} style={{ marginTop: -470 }}>
                            <Divider orientation="left" plain style={{ fontWeight: 'bold' }}>
                                Kemasan
                            </Divider>
                            <Space>
                                <Form.Item name="isconsole" valuePropName="checked" noStyle>
                                    <Checkbox>Console</Checkbox>
                                </Form.Item>
                                <Form.Item
                                    name="ispartial"
                                    valuePropName="checked"
                                    noStyle
                                >
                                    <Checkbox>Partial</Checkbox>
                                </Form.Item>
                            </Space>
                            <Form.Item
                                label="Jumlah Kemasan Terangkut"
                                name="jumlah_kemasan"
                                style={{ marginTop: 10 }}
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            return new Promise((resolve, reject) => {
                                                setTimeout(() => {
                                                    if (value !== undefined && value !== null && totalKemasan && value > totalKemasan) {
                                                        reject(new Error(`Jumlah kemasan tidak boleh lebih besar dari Total Kemasan (${totalKemasan})`));
                                                    } else {
                                                        resolve();
                                                    }
                                                }, 0);
                                            });
                                        }
                                    }
                                ]}
                            >
                                <InputNumber
                                    autoComplete="off"
                                    max={typeof totalKemasan === 'number' ? totalKemasan : undefined}
                                    min={0}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                            <Form.Item label="Jenis Kemasan" name="jenis_kemasan" style={{ marginTop: -15 }}>
                                <AutoComplete
                                    maxLength={2}
                                    options={kdKemas.map(item => ({
                                        value: item.value,
                                        label: `${item.label} - ${item.name}`
                                    }))}
                                    onInput={e => e.target.value = e.target.value.toUpperCase()}
                                    filterOption={(inputValue, option) => {
                                        const regex = new RegExp(inputValue, 'i');
                                        return regex.test(option.value);
                                    }}
                                />
                            </Form.Item>
                            <Form.Item label="Total Kemasan" name="total_kemasan" style={{ marginTop: -15 }}>
                                <InputNumber autoComplete="off" disabled={!isPartial} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="Jumlah Peti Kemas Terangkut" name="jumlah_kontainer" style={{ marginTop: -15 }}>
                                <InputNumber autoComplete="off" disabled style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item label="Total Peti Kemas" name="total_kontainer" style={{ marginTop: -15 }}>
                                <InputNumber autoComplete="off" disabled={!isPartial} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item label="Bruto" name="bruto" style={{ marginTop: -15 }}>
                                <InputNumber autoComplete="off" />
                            </Form.Item>
                            <Form.Item label="Volume" name="volume" style={{ marginTop: -15 }}>
                                <InputNumber autoComplete="off" />
                            </Form.Item>
                            <Form.Item label="Mark" name="merk_kemasan"
                                style={{ marginTop: -15 }}
                                rules={[{ required: true, message: 'Mark wajib dipilih' }]}
                            >
                                <Input.TextArea
                                    autoSize={{ minRows: 3, maxRows: 6 }}
                                    autoComplete="off"
                                    onInput={(e) => e.target.value = e.target.value.toUpperCase()}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Divider orientation="left" plain style={{ fontWeight: 'bold' }}>
                                        HS Code
                                    </Divider>
                                    <Form.List name="hs_codes">
                                        {(fields, { add, remove }) => {
                                            const tableData = fields.map(field => ({
                                                key: field.key,
                                                fieldName: field.name,
                                                ...(hsCodesValues?.[field.name] || {}),
                                            }));

                                            const handleHsModalSubmit = () => {
                                                hsModalForm
                                                    .validateFields()
                                                    .then(values => {
                                                        const isEditing = editingHsFieldIndex !== null && editingHsFieldIndex !== undefined;
                                                        if (isEditing) {
                                                            const currentHs = modalDetilForm.getFieldValue('hs_codes') || [];
                                                            const updatedHs = [...currentHs];
                                                            updatedHs[editingHsFieldIndex] = {
                                                                ...updatedHs[editingHsFieldIndex],
                                                                ...values,
                                                            };
                                                            modalDetilForm.setFieldsValue({ hs_codes: updatedHs });
                                                        } else {
                                                            add(values);
                                                        }
                                                        handleCloseHsModal();
                                                    })
                                                    .catch(() => { });
                                            };

                                            const isEditingHs = editingHsFieldIndex !== null && editingHsFieldIndex !== undefined;

                                            return (
                                                <>
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                                                        <Button
                                                            type="dashed"
                                                            onClick={() => handleOpenHsModal()}
                                                            icon="+"
                                                        >
                                                            Tambah HS Code
                                                        </Button>
                                                    </div>
                                                    <Table
                                                        size="small"
                                                        dataSource={tableData}
                                                        pagination={false}
                                                        rowKey="key"
                                                        bordered
                                                        columns={[
                                                            {
                                                                title: 'HS Code',
                                                                dataIndex: 'hs_code',
                                                                render: (value) => value || '-',
                                                            },
                                                            {
                                                                title: 'Uraian Barang',
                                                                dataIndex: 'uraian_barang',
                                                                render: (value) => value || '-',
                                                            },
                                                            {
                                                                title: 'Aksi',
                                                                render: (_, record) => (
                                                                    <Space>
                                                                        <Button
                                                                            type="link"
                                                                            onClick={() => {
                                                                                const { fieldName, ...rest } = record;
                                                                                handleOpenHsModal(rest, fieldName);
                                                                            }}
                                                                        >
                                                                            Edit
                                                                        </Button>
                                                                        <Button danger onClick={() => remove(record.fieldName)}>
                                                                            Hapus
                                                                        </Button>
                                                                    </Space>
                                                                ),
                                                            },
                                                        ]}
                                                    />
                                                    <Modal
                                                        title={isEditingHs ? "Edit HS Code" : "Tambah HS Code"}
                                                        visible={isHsModalVisible}
                                                        onCancel={handleCloseHsModal}
                                                        onOk={handleHsModalSubmit}
                                                        okText="Simpan"
                                                        cancelText="Batal"
                                                    >
                                                        <Form form={hsModalForm} layout="vertical">
                                                            <Form.Item
                                                                label="HS Code"
                                                                name="hs_code"
                                                                rules={[{ required: true, message: 'HS Code wajib dipilih' }]}
                                                            >
                                                                <AutoComplete
                                                                    options={hsAutocompleteOptions}
                                                                    allowClear
                                                                    onSelect={handleSelectHsAutocomplete}
                                                                    filterOption={(inputValue, option) =>
                                                                        option?.label?.toUpperCase().includes(inputValue.toUpperCase())
                                                                    }
                                                                >
                                                                    <Input
                                                                        autoComplete="off"
                                                                        placeholder="Pilih / ketik HS Code"
                                                                        onInput={e => (e.target.value = e.target.value.toUpperCase())}
                                                                        suffix={renderMasterHsIcon()}
                                                                    />
                                                                </AutoComplete>
                                                            </Form.Item>
                                                            <Form.Item label="Uraian Barang" name="uraian_barang">
                                                                <Input.TextArea autoSize={{ minRows: 3, maxRows: 3 }} autoComplete="off" />
                                                            </Form.Item>
                                                        </Form>
                                                    </Modal>
                                                </>
                                            );
                                        }}
                                    </Form.List>
                                </Col>

                                <Col span={12}>
                                    <Divider orientation="left" plain style={{ fontWeight: 'bold' }}>
                                        Dokumen
                                    </Divider>

                                    <Form.List name="dokumen">
                                        {(fields, { add, remove }) => {
                                            const tableData = fields.map(field => ({
                                                key: field.key,
                                                fieldName: field.name,
                                                ...(dokumenValues?.[field.name] || {}),
                                            }));

                                            const handleDokumenModalSubmit = () => {
                                                dokumenModalForm
                                                    .validateFields()
                                                    .then(values => {
                                                        const formattedValues = {
                                                            ...values,
                                                            tanggal_dokumen: values?.tanggal_dokumen
                                                                ? values.tanggal_dokumen.format('DD-MM-YYYY')
                                                                : '',
                                                        };
                                                        const isEditing = editingDokumenFieldIndex !== null && editingDokumenFieldIndex !== undefined;
                                                        if (isEditing) {
                                                            const currentDocs = modalDetilForm.getFieldValue('dokumen') || [];
                                                            const updatedDocs = [...currentDocs];
                                                            updatedDocs[editingDokumenFieldIndex] = {
                                                                ...updatedDocs[editingDokumenFieldIndex],
                                                                ...formattedValues,
                                                            };
                                                            modalDetilForm.setFieldsValue({ dokumen: updatedDocs });
                                                        } else {
                                                            add(formattedValues);
                                                        }
                                                        handleCloseDokumenModal();
                                                    })
                                                    .catch(() => { });
                                            };

                                            const isEditingDokumen = editingDokumenFieldIndex !== null && editingDokumenFieldIndex !== undefined;

                                            return (
                                                <>
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                                                        <Button
                                                            type="dashed"
                                                            onClick={() => handleOpenDokumenModal()}
                                                            icon="+"
                                                        >
                                                            Tambah Dokumen
                                                        </Button>
                                                    </div>
                                                    <Table
                                                        size="small"
                                                        dataSource={tableData}
                                                        pagination={false}
                                                        rowKey="key"
                                                        bordered
                                                        columns={[
                                                            {
                                                                title: 'Jenis Dokumen',
                                                                dataIndex: 'kode_dokumen',
                                                                render: (value) => {
                                                                    if (!value) return '-';
                                                                    const doc = jnsDokumen.find(item => item.value === value);
                                                                    return doc ? `${value} - ${doc.name}` : value;
                                                                }
                                                            },
                                                            {
                                                                title: 'Kode Kantor',
                                                                dataIndex: 'kode_kantor',
                                                                render: (value) => value || '-',
                                                            },
                                                            {
                                                                title: 'Nomor Dokumen',
                                                                dataIndex: 'nomor_dokumen',
                                                                render: (value) => value || '-',
                                                            },
                                                            {
                                                                title: 'Tanggal Dokumen',
                                                                dataIndex: 'tanggal_dokumen',
                                                                render: (value) => value || '-',
                                                            },
                                                            {
                                                                title: 'Aksi',
                                                                render: (_, record) => (
                                                                    <Space>
                                                                        <Button
                                                                            type="link"
                                                                            onClick={() => {
                                                                                const { fieldName, ...rest } = record;
                                                                                handleOpenDokumenModal(rest, fieldName);
                                                                            }}
                                                                        >
                                                                            Edit
                                                                        </Button>
                                                                        <Button danger onClick={() => remove(record.fieldName)}>
                                                                            Hapus
                                                                        </Button>
                                                                    </Space>
                                                                ),
                                                            },
                                                        ]}
                                                    />
                                                    <Modal
                                                        title={isEditingDokumen ? "Edit Dokumen" : "Tambah Dokumen"}
                                                        visible={isDokumenModalVisible}
                                                        onCancel={handleCloseDokumenModal}
                                                        onOk={handleDokumenModalSubmit}
                                                        okText="Simpan"
                                                        cancelText="Batal"
                                                    >
                                                        <Form form={dokumenModalForm} layout="vertical">
                                                            <Form.Item
                                                                label="Jenis Dokumen"
                                                                name="kode_dokumen"
                                                                rules={[{ required: true, message: 'Jenis dokumen wajib dipilih' }]}
                                                            >
                                                                <AutoComplete
                                                                    options={jnsDokumen.map(item => ({
                                                                        value: item.value,
                                                                        label: `${item.value} - ${item.name}`
                                                                    }))}
                                                                    onInput={e => e.target.value = e.target.value.toUpperCase()}
                                                                    filterOption={(inputValue, option) =>
                                                                        option?.label?.toUpperCase().includes(inputValue.toUpperCase())
                                                                    }
                                                                />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label="Kode Kantor"
                                                                name="kode_kantor"
                                                                rules={[{ required: true, message: 'Kode kantor wajib diisi' }]}
                                                            >
                                                                <AutoComplete
                                                                    options={kodeKantor.map(item => ({
                                                                        value: item.value,
                                                                        label: `${item.value} - ${item.name}`
                                                                    }))}
                                                                    onInput={e => e.target.value = e.target.value.toUpperCase()}
                                                                    filterOption={(inputValue, option) =>
                                                                        option?.label?.toUpperCase().includes(inputValue.toUpperCase())
                                                                    }
                                                                />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label="Nomor Dokumen"
                                                                name="nomor_dokumen"
                                                                rules={[{ required: true, message: 'Nomor dokumen wajib diisi' }]}
                                                            >
                                                                <Input autoComplete="off" />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label="Tanggal Dokumen"
                                                                name="tanggal_dokumen"
                                                                rules={[{ required: true, message: 'Tanggal dokumen wajib dipilih' }]}
                                                            >
                                                                <DatePicker
                                                                    placeholder=''
                                                                    format="DD-MM-YYYY"
                                                                    style={{ width: '100%' }}
                                                                />
                                                            </Form.Item>
                                                        </Form>
                                                    </Modal>
                                                </>
                                            );
                                        }}
                                    </Form.List>
                                </Col>
                            </Row>
                        </Col>

                        <Col span={24}>
                            <Divider orientation="left" plain style={{ fontWeight: 'bold' }}>
                                Kontainer
                            </Divider>
                            <Form.List name="kontainer">
                                {(fields, { add, remove }) => {
                                    const tableData = fields.map(field => ({
                                        key: field.key,
                                        fieldName: field.name,
                                        ...(kontainerValues?.[field.name] || {}),
                                    }));

                                    const handleKontainerModalSubmit = () => {
                                        kontainerModalForm
                                            .validateFields()
                                            .then(values => {
                                                const generatedId = values.id_kontainer || `${moment().valueOf()}`;
                                                const enrichedValues = {
                                                    ...values,
                                                    id_detil: values.id_detil || currentDetailId() || '',
                                                    id_kontainer: generatedId,
                                                };
                                                const isEditing = editingKontainerFieldIndex !== null && editingKontainerFieldIndex !== undefined;
                                                if (isEditing) {
                                                    const currentData = modalDetilForm.getFieldValue('kontainer') || [];
                                                    const updatedData = [...currentData];
                                                    updatedData[editingKontainerFieldIndex] = {
                                                        ...updatedData[editingKontainerFieldIndex],
                                                        ...enrichedValues,
                                                    };
                                                    modalDetilForm.setFieldsValue({ kontainer: updatedData });
                                                } else {
                                                    add(enrichedValues);
                                                }
                                                handleCloseKontainerModal();
                                            })
                                            .catch(() => { });
                                    };

                                    const isEditingKontainer = editingKontainerFieldIndex !== null && editingKontainerFieldIndex !== undefined;

                                    return (
                                        <>
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                                                <Button
                                                    type="dashed"
                                                    onClick={() => handleOpenKontainerModal()}
                                                    icon="+"
                                                >
                                                    Tambah Kontainer
                                                </Button>
                                            </div>
                                            <Table
                                                size="small"
                                                dataSource={tableData}
                                                pagination={false}
                                                rowKey="key"
                                                bordered
                                                scroll={{ x: true }}
                                                columns={[
                                                    {
                                                        title: 'ID Kontainer',
                                                        dataIndex: 'id_kontainer',
                                                        render: (value) => value || '-',
                                                    },
                                                    {
                                                        title: 'ID Detil',
                                                        dataIndex: 'id_detil',
                                                        render: (value) => value || '-',
                                                    },
                                                    {
                                                        title: 'Seri',
                                                        dataIndex: 'seri_kontainer',
                                                        render: (value) => value || '-',
                                                    },
                                                    {
                                                        title: 'Nomor',
                                                        dataIndex: 'nomor_kontainer',
                                                        render: (value) => value || '-',
                                                    },
                                                    {
                                                        title: 'Ukuran',
                                                        dataIndex: 'ukuran_kontainer',
                                                        render: (value) => value || '-',
                                                    },
                                                    {
                                                        title: 'Tipe',
                                                        dataIndex: 'tipe_kontainer',
                                                        render: (value) => value || '-',
                                                    },
                                                    {
                                                        title: 'Jenis',
                                                        dataIndex: 'jenis_kontainer',
                                                        render: (value) => value || '-',
                                                    },
                                                    {
                                                        title: 'Nomor Segel',
                                                        dataIndex: 'nomor_segel',
                                                        render: (value) => value || '-',
                                                    },
                                                    {
                                                        title: 'Status',
                                                        dataIndex: 'status_kontainer',
                                                        render: (value) => value || '-',
                                                    },
                                                    {
                                                        title: 'Aksi',
                                                        render: (_, record) => (
                                                            <Space>
                                                                <Button
                                                                    type="link"
                                                                    onClick={() => {
                                                                        const { fieldName, ...rest } = record;
                                                                        handleOpenKontainerModal(rest, fieldName);
                                                                    }}
                                                                >
                                                                    Edit
                                                                </Button>
                                                                <Button danger onClick={() => remove(record.fieldName)}>
                                                                    Hapus
                                                                </Button>
                                                            </Space>
                                                        ),
                                                    },
                                                ]}
                                            />
                                            <Modal
                                                title={isEditingKontainer ? "Edit Kontainer" : "Tambah Kontainer"}
                                                visible={isKontainerModalVisible}
                                                onCancel={handleCloseKontainerModal}
                                                onOk={handleKontainerModalSubmit}
                                                okText="Simpan"
                                                cancelText="Batal"
                                                width={600}
                                            >
                                                <Form form={kontainerModalForm} layout="vertical">
                                                    <Form.Item name="id_kontainer" hidden>
                                                        <Input autoComplete="off" />
                                                    </Form.Item>
                                                    <Form.Item name="id_detil" hidden>
                                                        <Input autoComplete="off" />
                                                    </Form.Item>
                                                    <Form.Item
                                                        label="Seri Kontainer"
                                                        name="seri_kontainer"
                                                        hidden
                                                    >
                                                        <Input autoComplete="off" />
                                                    </Form.Item>
                                                    <Form.Item
                                                        label="Nomor Kontainer"
                                                        name="nomor_kontainer"
                                                        rules={[{ required: true, message: 'Nomor kontainer wajib diisi' }]}
                                                    >
                                                        <Input autoComplete="off" style={{ width: '100%' }}
                                                            onInput={e => e.target.value = e.target.value.toUpperCase()}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item
                                                        label="Ukuran Kontainer"
                                                        name="ukuran_kontainer"
                                                        rules={[{ required: true, message: 'Ukuran kontainer wajib dipilih' }]}
                                                    >
                                                        <Select allowClear>
                                                            <Option value="20">20 FEET</Option>
                                                            <Option value="40">40 FEET</Option>
                                                            <Option value="45">45 FEET</Option>
                                                        </Select>
                                                    </Form.Item>
                                                    <Form.Item
                                                        label="Tipe Kontainer"
                                                        name="tipe_kontainer"
                                                        rules={[{ required: true, message: 'Tipe kontainer wajib dipilih' }]}
                                                    >
                                                        <Select allowClear>
                                                            <Option value="1">1 - General / Dry Cargo</Option>
                                                            <Option value="2">2 - Tunne Type</Option>
                                                            <Option value="3">3 - Open Top Steel</Option>
                                                            <Option value="4">4 - Flat Rack</Option>
                                                            <Option value="5">5 - Reefer / Refrigerated</Option>
                                                            <Option value="6">6 - Barge Container</Option>
                                                            <Option value="7">7 - Bulk Container</Option>
                                                            <Option value="8">8 - ISO Tank</Option>
                                                            <Option value="99">99 - Lain-lain</Option>
                                                        </Select>
                                                    </Form.Item>
                                                    <Form.Item
                                                        label="Jenis Kontainer"
                                                        name="jenis_kontainer"
                                                        rules={[{ required: true, message: 'Jenis kontainer wajib dipilih' }]}
                                                    >
                                                        <Select allowClear>
                                                            <Option value="E">E - Empty Container</Option>
                                                            <Option value="F">F - FCL</Option>
                                                            <Option value="L">L - LCL</Option>
                                                        </Select>
                                                    </Form.Item>
                                                    <Form.Item
                                                        label="Status Kontainer"
                                                        name="status_kontainer"
                                                        rules={[{ required: true, message: 'Status kontainer wajib dipilih' }]}
                                                    >
                                                        <Select allowClear>
                                                            <Option value="01">01 - Terangkut</Option>
                                                            <Option value="02">02 - Tertinggal</Option>
                                                        </Select>
                                                    </Form.Item>
                                                    <Form.Item
                                                        label="Nomor Segel"
                                                        name="nomor_segel"
                                                        rules={[{ required: true, message: 'Nomor segel wajib diisi' }]}
                                                    >
                                                        <Input autoComplete="off" />
                                                    </Form.Item>
                                                </Form>
                                            </Modal>
                                        </>
                                    );
                                }}
                            </Form.List>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title={`Master Consignee & Shipper${masterConsigneeTarget ? ` - ${targetLabelMap[masterConsigneeTarget]}` : ''}`}
                visible={isMasterConsigneeModalVisible}
                onCancel={handleCloseMasterConsigneeModal}
                footer={null}
                width={1000}
            >
                <p style={{ marginBottom: 16 }}>
                    Data terpilih akan mengisi bagian: <strong>{targetLabelMap[masterConsigneeTarget] || '-'}</strong>
                </p>
                <Tabs activeKey={masterConsigneeTab} onChange={setMasterConsigneeTab}>
                    <TabPane tab="Pilih Data" key="select">
                        <Input.Search
                            placeholder="Cari NPWP / Nama / Negara"
                            allowClear
                            value={consigneeSearchKeyword}
                            onChange={(e) => setConsigneeSearchKeyword(e.target.value)}
                            style={{ marginBottom: 12 }}
                        />
                        <Table
                            size="small"
                            dataSource={masterConsigneeData}
                            rowKey={(record) => record?._id || record?.id || record?.npwp || record?.name}
                            pagination={{ pageSize: 8 }}
                            onRow={(record) => ({
                                onDoubleClick: () => handleSelectConsigneeFromMaster(record)
                            })}
                            columns={[
                                {
                                    title: 'Aksi',
                                    key: 'action',
                                    width: 140,
                                    render: (_, record) => (
                                        <Space>
                                            <Tooltip title="Gunakan Data">
                                                <Button
                                                    type="text"
                                                    icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectConsigneeFromMaster(record);
                                                    }}
                                                />
                                            </Tooltip>
                                            <Tooltip title="Edit Data">
                                                <Button
                                                    type="text"
                                                    icon={<EditOutlined style={{ color: '#faad14' }} />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditMasterConsignee(record);
                                                    }}
                                                />
                                            </Tooltip>
                                        </Space>
                                    ),
                                },
                                {
                                    title: 'NPWP',
                                    dataIndex: 'npwp',
                                    width: 160,
                                },
                                {
                                    title: 'Nama',
                                    dataIndex: 'name',
                                },
                                {
                                    title: 'Negara',
                                    dataIndex: 'country',
                                    width: 120,
                                },
                                {
                                    title: 'Alamat',
                                    dataIndex: 'address',
                                    render: (_, record) => extractAddressList(record).join(', ')
                                }
                            ]}
                        />
                    </TabPane>
                    <TabPane tab="Tambah / Edit Data" key="add">
                        <Form form={masterConsigneeForm} layout="vertical" onFinish={handleSubmitMasterConsignee}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="name" label="Nama" rules={[{ required: true, message: 'Nama wajib diisi' }]}>
                                        <Input autoComplete="off"
                                            onInput={e => e.target.value = e.target.value.toUpperCase()}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="npwp" label="NPWP" rules={[{ required: true, message: 'NPWP wajib diisi' }]}>
                                        <Input autoComplete="off" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="country" label="Negara" rules={[{ required: true, message: 'Negara wajib diisi' }]}>
                                        <Input autoComplete="off" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="address" label="Alamat" rules={[{ required: true, message: 'Alamat wajib diisi' }]}>
                                        <Input autoComplete="off" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                                <Space>
                                    <Button onClick={handleResetMasterConsigneeForm}>
                                        Reset
                                    </Button>
                                    <Button type="primary" htmlType="submit">
                                        Simpan
                                    </Button>
                                </Space>
                            </div>
                        </Form>
                    </TabPane>
                </Tabs>
            </Modal>

            <Modal
                title="Master HS Code"
                visible={isMasterHsModalVisible}
                onCancel={handleCloseMasterHsModal}
                footer={null}
                width={800}
                zIndex={1300}
            >
                <Tabs activeKey={masterHsTab} onChange={handleMasterHsTabChange}>
                    <TabPane tab="Pilih Data" key="select">
                        <Input.Search
                            placeholder="Cari HS Code / Uraian"
                            allowClear
                            value={hsSearchKeyword}
                            onChange={(e) => setHsSearchKeyword(e.target.value)}
                            style={{ marginBottom: 12 }}
                        />
                        <Table
                            size="small"
                            dataSource={masterHsData}
                            rowKey={(record) => record?._id || record?.id || record?.hs_code}
                            pagination={{ pageSize: 8 }}
                            onRow={(record) => ({
                                onDoubleClick: () => handleSelectHsFromMaster(record)
                            })}
                            columns={[
                                {
                                    title: 'Aksi',
                                    key: 'action',
                                    width: 140,
                                    render: (_, record) => (
                                        <Space>
                                            <Tooltip title="Gunakan Data">
                                                <Button
                                                    type="text"
                                                    icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectHsFromMaster(record);
                                                    }}
                                                />
                                            </Tooltip>
                                            <Tooltip title="Edit Data">
                                                <Button
                                                    type="text"
                                                    icon={<EditOutlined style={{ color: '#faad14' }} />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditMasterHs(record);
                                                    }}
                                                />
                                            </Tooltip>
                                        </Space>
                                    ),
                                },
                                {
                                    title: 'HS Code',
                                    dataIndex: 'hs_code',
                                    width: 140,
                                },
                                {
                                    title: 'Uraian Barang',
                                    dataIndex: 'hs_code_description',
                                },
                            ]}
                        />
                    </TabPane>
                    <TabPane tab="Tambah / Edit Data" key="add">
                        <Form form={masterHsForm} layout="vertical" onFinish={handleSubmitMasterHs}>
                            <Form.Item
                                name="hs_code"
                                label="HS Code"
                                rules={[{ required: true, message: 'HS Code wajib diisi' }]}
                            >
                                <Input autoComplete="off" onInput={(e) => e.target.value = e.target.value.toUpperCase()} />
                            </Form.Item>
                            <Form.Item
                                name="hs_code_description"
                                label="Uraian Barang"
                                rules={[{ required: true, message: 'Uraian Barang wajib diisi' }]}
                            >
                                <Input.TextArea autoSize={{ minRows: 3, maxRows: 3 }} autoComplete="off" />
                            </Form.Item>
                            <Space>
                                <Button type="primary" htmlType="submit">
                                    {editingMasterHs ? 'Simpan Perubahan' : 'Simpan'}
                                </Button>
                                <Button onClick={handleResetMasterHsForm}>
                                    Reset
                                </Button>
                            </Space>
                        </Form>
                    </TabPane>
                </Tabs>
            </Modal>
        </div >
    )
}

export default BillModal